import type { components } from '@multaqa/api-client'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'

export type StockValuationReport = components['schemas']['StockValuationReportResource']
export type StockValuationRow = components['schemas']['StockValuationRowResource']
export type StockValuationTotal = components['schemas']['StockValuationTotalResource']
export type StockValuationLayers = components['schemas']['StockValuationLayersResource']
export type StockValuationLayer = components['schemas']['StockValuationLayerResource']
export type ValuationIntegrityReport = components['schemas']['ValuationIntegrityReportResource']

export const COSTING_METHODS = ['standard', 'average', 'fifo'] as const
export type CostingMethod = (typeof COSTING_METHODS)[number]

export const STOCK_VALUATION_PAGE_SIZES = [25, 50, 100] as const
const DEFAULT_PAGE_SIZE = 50
export const LAYERS_PAGE_SIZE = 25

/** The URL-backed filters of the stock valuation report. */
export interface StockValuationSearch {
  warehouse_id?: string
  category_id?: string
  search?: string
  method?: CostingMethod
  page?: number
  per_page?: number
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value : undefined
}

function positive(value: unknown): number | undefined {
  const parsed = typeof value === 'number' ? value : Number(text(value) ?? Number.NaN)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined
}

export function isCostingMethod(value: unknown): value is CostingMethod {
  return typeof value === 'string' && (COSTING_METHODS as readonly string[]).includes(value)
}

export function stockValuationSearchFrom(search: Record<string, unknown>): StockValuationSearch {
  const perPage = positive(search.per_page)
  return {
    warehouse_id: text(search.warehouse_id),
    category_id: text(search.category_id),
    search: text(search.search),
    method: isCostingMethod(search.method) ? search.method : undefined,
    page: positive(search.page),
    per_page:
      perPage && (STOCK_VALUATION_PAGE_SIZES as readonly number[]).includes(perPage)
        ? perPage
        : undefined,
  }
}

export const valuationKeys = {
  report: (companyId: string | null, search: StockValuationSearch) =>
    ['inventory', companyId, 'reports', 'stock-valuation', search] as const,
  layers: (companyId: string | null, variantId: string, warehouseId: string, page: number) =>
    [
      'inventory',
      companyId,
      'reports',
      'stock-valuation',
      'layers',
      variantId,
      warehouseId,
      page,
    ] as const,
  integrity: (companyId: string | null) =>
    ['inventory', companyId, 'reports', 'valuation-integrity'] as const,
}

const companyHeader = (companyId: string) => ({ 'X-Company-Id': companyId })

export function pageSizeOf(search: StockValuationSearch): number {
  return search.per_page ?? DEFAULT_PAGE_SIZE
}

export function useStockValuation(companyId: string | null, search: StockValuationSearch) {
  return useQuery({
    queryKey: valuationKeys.report(companyId, search),
    enabled: Boolean(companyId),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<StockValuationReport> => {
      if (!companyId) throw new Error('An active company is required to load this report.')
      const response = await api.GET('/inventory/reports/stock-valuation', {
        headers: companyHeader(companyId),
        params: { query: { ...search, per_page: pageSizeOf(search) } },
      })
      if (!response.data) throw new Error('The stock valuation report returned no data.')
      return response.data.data
    },
  })
}

export function useStockValuationLayers(
  companyId: string | null,
  target: { variantId: string; warehouseId: string } | null,
  page: number,
) {
  return useQuery({
    queryKey: valuationKeys.layers(
      companyId,
      target?.variantId ?? '',
      target?.warehouseId ?? '',
      page,
    ),
    enabled: Boolean(companyId && target),
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<StockValuationLayers> => {
      if (!companyId || !target) throw new Error('A variant is required to load its layers.')
      const response = await api.GET('/inventory/reports/stock-valuation/{variantId}/layers', {
        headers: companyHeader(companyId),
        params: {
          path: { variantId: target.variantId },
          query: { warehouse_id: target.warehouseId, page, per_page: LAYERS_PAGE_SIZE },
        },
      })
      if (!response.data) throw new Error('The valuation layers returned no data.')
      return response.data.data
    },
  })
}

export function useValuationIntegrity(companyId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: valuationKeys.integrity(companyId),
    enabled: Boolean(companyId) && enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<ValuationIntegrityReport> => {
      if (!companyId) throw new Error('An active company is required to load this report.')
      const response = await api.GET('/inventory/reports/valuation-integrity', {
        headers: companyHeader(companyId),
      })
      if (!response.data) throw new Error('The valuation check returned no data.')
      return response.data.data
    },
  })
}

/** The export takes the report's filters, never its paging. */
export function stockValuationExportFilters(search: StockValuationSearch) {
  const { page: _page, per_page: _perPage, ...filters } = search
  return filters
}
